// MUI
import { Box } from "@mui/material";

// Third Party
import { Bar, BarChart, Legend, ReferenceLine, Tooltip, XAxis, YAxis } from "recharts";

// Components
import { CustomizedTooltip } from "@Layouts/components/Charts/GroupedBarChart/Widgets/ChartBarCustomizedTooltip";

// Helpers
import { formatNumberCompact } from "@Common/helpers/number-new";
import { CustomXAxisTick } from "@Layouts/components/Charts/GroupedBarChart/Widgets/CustomXAxisTick";
import { buildNiceTicks, createGroupedBar, formatCount } from "@Layouts/components/Charts/GroupedBarChart/utils";
// Types
import { AxisConfig, GroupedBarChartProps } from "@Layouts/components/Charts/GroupedBarChart/types.ts";

// Default colors palette
const DEFAULT_COLORS = ["#FFB507", "#2357BE", "#009245", "#E91E63", "#9C27B0", "#FF5722", "#795548"];

export default function GroupedBarChart<T extends Record<string, any>>(props: Readonly<GroupedBarChartProps<T>>) {
	const {
		syncId,
		data,
		bars,
		totalKey,
		parentBarColor = "#ECF0F6",
		xAxis,
		yAxis,
		colors = DEFAULT_COLORS,
		dimensions = { width: 650, height: 280 },
		tickConfig = {},
		tooltip,
		legend,
		referenceLine,
		customBarShape,
		hoveredValue,
		onBarHover,
		onBarLeave,
		onBarClick,
	} = props;

	// Calculate max value for Y axis
	const maxDataValue = Math.max(
		0,
		...(data ?? []).map((item) => {
			return totalKey ? (item[totalKey] as number) : bars.reduce((sum, bar) => sum + ((item[bar.dataKey] as number) || 0), 0);
		})
	);

	const maxTotalY = Math.max(0, yAxis.maxValue || 0, maxDataValue);
	const { ticks, domainMax } = buildNiceTicks(maxTotalY, tickConfig.minTicks || 5, tickConfig.headroomRatio || 0.03);

	// Default tick formatter
	const defaultTickFormatter =
		yAxis.tickFormatter ||
		((value: number) =>
			yAxis.label?.toLowerCase().includes("amount") || yAxis.label?.toLowerCase().includes("$")
				? formatNumberCompact(value ?? "0")
				: formatCount(value));

	/********************************************* HANDLERS ************************************************/
	const handleBarMouseEnter = (barData: T) => {
		const xValue = String(barData[xAxis.dataKey]);
		onBarHover?.(xValue, barData as T);
	};

	const handleBarMouseLeave = () => {
		onBarLeave?.();
	};

	const handleBarClick = (barData: T) => {
		const xValue = String(barData[xAxis.dataKey]);
		onBarClick?.(xValue, barData);
	};

	// Create the grouped bar shape
	const GroupedBarShape = customBarShape || createGroupedBar(bars, colors, parentBarColor, totalKey);

	// Get all X-axis values to determine previous value
	const allXValues = (data ?? []).map((item) => String(item[xAxis.dataKey]));

	return (
		<Box width="100%">
			<BarChart
				syncId={syncId}
				width={dimensions.width}
				height={dimensions.height}
				data={data ?? []}
				margin={{ top: 5, right: 10, left: 5, bottom: 20 }}
				barCategoryGap={1}
			>
				{/* X Axis */}
				<XAxis
					dataKey={String(xAxis.dataKey)}
					tick={<CustomXAxisTick hoveredValue={hoveredValue} config={xAxis as AxisConfig<{}>} allXValues={allXValues} />}
					tickLine={false}
					label={
						xAxis.label && {
							value: xAxis.label,
							position: xAxis.position || "insideBottomLeft",
							offset: xAxis.offset || -5,
							fill: xAxis.fill || "#666666",
							fontSize: xAxis.fontSize || 12,
							fontWeight: xAxis.fontWeight || "bold",
						}
					}
				/>

				{/* Y Axis */}
				<YAxis
					label={
						yAxis.label && {
							value: yAxis.label,
							angle: yAxis.angle || -90,
							position: yAxis.position || "insideLeft",
							fill: yAxis.fill || "#666666",
							fontSize: yAxis.fontSize || 12,
							fontWeight: yAxis.fontWeight || "bold",
						}
					}
					domain={yAxis.domain || [0, domainMax]}
					ticks={ticks}
					tickFormatter={defaultTickFormatter}
					allowDecimals={false}
					stroke={yAxis.stroke || "#666666"}
					width={yAxis.width || 60}
				/>

				{/* Tooltip */}
				{tooltip?.content ? (
					<Tooltip
						cursor={tooltip.cursor ? tooltip.cursor : false}
						formatter={tooltip.formatter}
						labelFormatter={tooltip.labelFormatter}
						animationDuration={0}
					/>
				) : tooltip?.useCustomizedTooltip ? (
					<Tooltip
						content={() => {
							// Find the hovered data item
							const hoveredData = data?.find((item) => String(item[xAxis.dataKey]) === hoveredValue);
							if (!hoveredData) {
								return null;
							}

							// Build dynamic tooltip data from bars configuration
							const tooltipBars = bars.map((bar, index) => ({
								label: bar.label || String(bar.dataKey),
								value: (hoveredData[bar.dataKey] as number) || 0,
								color: bar.color || colors[index] || colors[0],
							}));

							return (
								<CustomizedTooltip
									bars={tooltipBars}
									showTotal={tooltip.showTotal !== false}
									formatter={tooltip.valueFormatter}
								/>
							);
						}}
						cursor={tooltip.cursor ? tooltip.cursor : false}
						animationDuration={0}
					/>
				) : (
					<Tooltip cursor={false} />
				)}

				{/* Legend */}
				{legend?.enabled && (
					<Legend
						align={legend.align}
						verticalAlign={legend.verticalAlign}
						layout={legend.layout}
						iconType={legend.iconType}
						wrapperStyle={legend.wrapperStyle}
					/>
				)}

				{/* Reference Line */}
				{referenceLine?.enabled && (
					<ReferenceLine
						y={referenceLine.value}
						stroke={referenceLine.stroke || "#ff0000"}
						strokeDasharray={referenceLine.strokeDasharray || "5 5"}
						label={referenceLine.label}
					/>
				)}

				{/* Grouped Bars */}
				<Bar
					dataKey={String(totalKey || bars[0].dataKey)}
					// @ts-ignore
					shape={(shapeProps) => <GroupedBarShape {...shapeProps} />}
					background={{ fill: "transparent" }}
					onMouseEnter={handleBarMouseEnter}
					onMouseLeave={handleBarMouseLeave}
					onClick={onBarClick && handleBarClick}
				/>
			</BarChart>
		</Box>
	);
}
